import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SurfaceTier } from '@app/core/models/types/surface-tier.type';
import { SurfaceTierThemeService, SurfaceTierThemeType } from '@app/core/services/surface-tier-theme.service';
import { SurfaceUpgradeFeatureProp } from '@app/core/services/surface-upgrade-feature.service';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';

@Component({
    selector: 'fdx-surface-tier-icon-button',
    templateUrl: './surface-tier-icon-button.component.html',
    styleUrl: './surface-tier-icon-button.component.scss',
    standalone: false
})
export class SurfaceTierIconButtonComponent implements OnChanges, OnInit, AfterViewInit {

    /**
     * Whether or not to render this component as a button.
     * Defaults to true.
     */
    @Input() clickable: boolean = true;
    /**
     * Which tier to use for this button or badge.
     * Defaults to 'retail'.
     */
    @Input() tier: SurfaceTier = 'retail';
    @Input() size: 'sm' | 'md' | 'xl' = 'md';

    /**
     * Required if showing the secondary button that should link to the channel assignments page
     */
    @Input() currentExport?: Partial<ExportInterface>;

    /**
     * Required if clickable
     */
    @Input() feature?: SurfaceUpgradeFeatureProp;
    /**
     * Required if clickable
     */
    @Input() popoverPlacement?: PlacementArray = 'auto';
    /**
     * If clickable and not overriding the target, this will be the button element.
     */
    @Input() target?: HTMLElement;

    @Output() readonly popoverShown: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly popoverHidden: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('buttonElement') buttonElement: ElementRef<HTMLElement>;
    @ViewChild('spanElement') spanElement: ElementRef<HTMLElement>;

    tierThemeType: SurfaceTierThemeType;

    get icon(): IconDefinition {
        if (!this.tierThemeType?.icon) {
            return null;
        }

        return this.tierThemeType.icon;
    }

    get image(): string {
        if (!this.tierThemeType?.image) {
            return null;
        }

        return this.tierThemeType.image;
    }

    get tierThemeClasses(): string {
        if (!this.tierThemeType) {
            return '';
        }

        let buttonSize: string = '';
        let badgeSize: string = '';

        switch (this.size) {
            case 'sm':
                buttonSize = 'btn-sm';
                badgeSize = 'badge-sm';
                break;
            case 'xl':
                buttonSize = 'btn-xl';
                badgeSize = 'badge-xl';
                break;
            default:
                break;
        }

        if (this.clickable) {
            return `btn-${this.tierThemeType.tier} ${buttonSize}`;
        }

        return `text-bg-${this.tierThemeType.tier} ${badgeSize}`;
    }

    constructor(
        private readonly surfaceThemeService: SurfaceTierThemeService
    ) {}

    ngOnInit(): void {
        // Init tier with potential default value
        this.tierThemeType = this.surfaceThemeService.getTierThemeType(this.tier);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('tier' in changes) {
            this.tierThemeType = this.surfaceThemeService.getTierThemeType(this.tier);
        }
    }

    ngAfterViewInit(): void {
        if (this.buttonElement && !this.target) {
            this.target = this.buttonElement.nativeElement;
        }
    }
}
